import { z } from 'zod';
import { LATERALIDADES, ORIGENES_DE_DATO, } from '../catalogos/nucleo.js';
import { CODIGOS_DE_OBSERVACION, DEFINICION_DE_OBSERVACION, UNIDADES, aUnidadCanonica, } from '../catalogos/observaciones.js';
import { esquemaId } from './comunes.js';
import { esquemaInstante } from './cita.js';
/**
 * Observaciones y mediciones (F2.1, plan maestro §5.3).
 *
 * Cada una registra codigo, valor, unidad, cuando se midio, de donde viene y su
 * estado de revision. El esquema valida contra el catalogo: la unidad tiene que
 * ser de esa observacion, la lateralidad aparece si y solo si la pide, y el
 * valor cae dentro de los limites de captura.
 */
/**
 * Estado de revision. Lo que registra el profesional no requiere revision; lo
 * que registra la paciente queda pendiente hasta que alguien lo revise, y no se
 * convierte en dato profesional por si solo (plan §13.1, «Seguimiento»).
 */
export const ESTADOS_DE_REVISION = [
    'no-requiere',
    'pendiente',
    'revisada',
    'descartada',
    'corregida',
];
const esquemaComponentes = z.record(z.string().trim().min(1).max(40), z.number().finite());
export const esquemaRegistrarObservacion = z
    .object({
    pacienteId: esquemaId,
    encuentroId: esquemaId.optional(),
    codigo: z.enum(CODIGOS_DE_OBSERVACION),
    /** Para observaciones de forma `cantidad`. */
    valor: z.number().finite().optional(),
    /** Para observaciones de forma `componentes` (p. ej. sistolica y diastolica). */
    componentes: esquemaComponentes.optional(),
    unidad: z.enum(UNIDADES),
    lateralidad: z.enum(LATERALIDADES).optional(),
    /** Tiempo clinico: cuando se midio. El de registro lo pone el servidor. */
    medidaEn: esquemaInstante,
    metodo: z.string().trim().max(120).default(''),
    origen: z.enum(ORIGENES_DE_DATO).default('profesional'),
    /** Repetir la misma clave no duplica la observacion (plan §13.3). */
    claveIdempotencia: z.string().trim().min(8).max(100).optional(),
    nota: z.string().trim().max(500).default(''),
})
    .strict()
    .superRefine((observacion, ctx) => {
    for (const problema of problemasDeObservacion(observacion)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: problema.mensaje,
            path: [problema.campo],
        });
    }
});
/**
 * Que no cuadra de una observacion contra su definicion. Exportado para que el
 * servidor y la interfaz digan exactamente lo mismo.
 */
export function problemasDeObservacion(observacion) {
    const definicion = DEFINICION_DE_OBSERVACION[observacion.codigo];
    const problemas = [];
    if (!definicion.unidades.includes(observacion.unidad)) {
        problemas.push({
            campo: 'unidad',
            mensaje: `${definicion.nombre} no se mide en esa unidad`,
        });
        return problemas;
    }
    if (definicion.lateralidad === 'requerida' && !observacion.lateralidad) {
        problemas.push({ campo: 'lateralidad', mensaje: 'Indique el lado' });
    }
    if (definicion.lateralidad === 'no-aplica' && observacion.lateralidad) {
        problemas.push({ campo: 'lateralidad', mensaje: `${definicion.nombre} no lleva lado` });
    }
    if (observacion.lateralidad &&
        definicion.lados &&
        !definicion.lados.includes(observacion.lateralidad)) {
        problemas.push({ campo: 'lateralidad', mensaje: `${definicion.nombre} es de un solo lado` });
    }
    if (definicion.metodos) {
        const metodo = observacion.metodo ?? '';
        if (!metodo) {
            problemas.push({ campo: 'metodo', mensaje: 'Indique el método o las condiciones' });
        }
        else if (!definicion.metodos.some((m) => m.clave === metodo)) {
            problemas.push({ campo: 'metodo', mensaje: 'Método desconocido para esta medición' });
        }
    }
    const fuera = (valor, rango) => {
        const canonico = aUnidadCanonica(observacion.codigo, valor, observacion.unidad);
        return canonico === null || canonico < rango.min || canonico > rango.max;
    };
    if (definicion.forma === 'cantidad') {
        if (observacion.componentes !== undefined) {
            problemas.push({ campo: 'componentes', mensaje: `${definicion.nombre} es un solo valor` });
        }
        if (observacion.valor === undefined) {
            problemas.push({ campo: 'valor', mensaje: 'Escriba el valor' });
        }
        else if (definicion.rango && fuera(observacion.valor, definicion.rango)) {
            problemas.push({ campo: 'valor', mensaje: 'El valor está fuera de lo capturable; revíselo' });
        }
        return problemas;
    }
    // Forma `componentes`: exactamente las partes del catalogo.
    if (observacion.valor !== undefined) {
        problemas.push({ campo: 'valor', mensaje: `${definicion.nombre} se captura por partes` });
    }
    const partes = observacion.componentes ?? {};
    const esperadas = definicion.componentes ?? [];
    for (const componente of esperadas) {
        const valor = partes[componente.clave];
        if (valor === undefined) {
            problemas.push({ campo: 'componentes', mensaje: `Falta: ${componente.nombre}` });
        }
        else if (fuera(valor, componente.rango)) {
            problemas.push({
                campo: 'componentes',
                mensaje: `${componente.nombre} está fuera de lo capturable; revíselo`,
            });
        }
    }
    for (const clave of Object.keys(partes)) {
        if (!esperadas.some((componente) => componente.clave === clave)) {
            problemas.push({ campo: 'componentes', mensaje: `Parte desconocida: ${clave}` });
        }
    }
    return problemas;
}
export const esquemaListarObservaciones = z.object({
    pacienteId: esquemaId,
    codigo: z.enum(CODIGOS_DE_OBSERVACION).optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    porPagina: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=observacion.js.map